import { useQueryClient } from 'react-query'

import { useQueryApi, useMutationApi } from 'utils/api'
import {
  apiPaths,
  handleError,
  queryKeys,
  validateStatus,
} from 'api/kinerja/penilik-pegawai/config'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { AxiosError } from 'axios'
import type {
  PenilikSummary,
  PenilikStatus,
  RequestMulaiPelaksanaanBody,
  ResponseSyncToEkin,
  ResetSKPPhase,
} from 'types/kinerja-penilik-pegawai'
import type { UseQueryOptions } from 'react-query'

export function useAPIGetSummary({
  periodId,
  ...options
}: UseQueryOptions<PenilikSummary> & { periodId: string }) {
  return useQueryApi<PenilikSummary>(apiPaths.summary(periodId), {
    queryKey: [queryKeys.API_GET_SUMMARY, periodId],
    axiosConfig: {
      validateStatus,
    },
    queryConfig: {
      useErrorBoundary: false,
      ...options,
    },
    errorHandlerFn: handleError,
  })
}

export function useAPIGetStatus({
  periodId,
  ...options
}: UseQueryOptions<PenilikStatus> & { periodId: string }) {
  return useQueryApi<PenilikStatus>(apiPaths.status(periodId), {
    queryKey: [queryKeys.API_GET_STATUS, periodId],
    axiosConfig: {
      validateStatus,
    },
    queryConfig: {
      useErrorBoundary: false,
      ...options,
    },
    errorHandlerFn: handleError,
  })
}

export function useAPISyncToEkin() {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<
    ResponseSyncToEkin,
    AxiosError,
    RequestMulaiPelaksanaanBody
  >(() => apiPaths.syncToEkin, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([queryKeys.API_GET_SUMMARY, period.id])
      },
    },
  })
}

export function useAPIResetSKP({ phase }: { phase: ResetSKPPhase }) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<{ message: string }, AxiosError, {}>(
    () => apiPaths.resetSkp(period.id, phase),
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PERENCANAAN,
            period.id,
          ])

          queryClient.invalidateQueries([
            queryKeys.API_GET_PERENCANAAN_SUMMARY,
            period.id,
          ])
        },
      },
    }
  )
}
